import { useShallow } from 'zustand/shallow'

import Slot from '@/components/atoms/ConfigSlot'
import { useScopedI18n } from '@/locales/client'
import useDialogsStore from '@/stores/DialogsStore'
import { cn } from '@/util/cn'
import { PRO_HUBS_IDS } from '@/util/Hardcoded'

type Props = {
  hubId: string
  className?: string
  isModal?: boolean
}

export default function ConfigPreview({ className, hubId, isModal }: Props) {
  const t = useScopedI18n('colorLegend')
  const isPro = PRO_HUBS_IDS.includes(hubId)

  const toggle = useDialogsStore(useShallow(state => state.toggle))
  const setConfigPreviewInfo = useDialogsStore(store => store.setConfigPreviewInfo)

  const handleClick = () => {
    setConfigPreviewInfo(hubId)
    toggle('configurationPreview')
  }

  return (
    <button
      className={cn('config-grid grid lg:gap-x-3 lg:gap-y-4 font-sans', className)}
      type="button"
      onClick={handleClick}
    >
      <Slot
        area="p1"
        title={isPro ? t('includedItem').replace('Lite', 'Pro') : t('includedItem')}
        image="controller-lite"
        variants={['pro', 'mobile']}
        state="selected"
        hubId={hubId}
        className="controller justify-end px-1 pb-0 pt-7"
      />

      <Slot
        rNumber="R35825"
        area="b2"
        height="fill"
        image="temp-probe"
        hubId={hubId}
      />
      <Slot
        rNumber="R35822"
        area="b3"
        height="fill"
        image="orp-probe"
        className="px-3 pb-3"
        hubId={hubId}
      />
      <Slot
        rNumber="R35826"
        area="b4"
        image="magnetic-holder"
        position="horizontal"
        className="holder px-11 md:px-8"
        hubId={hubId}
      />
      <Slot
        rNumber="R35821"
        area="b5"
        height="fill"
        image="ph-temp-probe"
        className="px-3 pb-3 "
        hubId={hubId}
      />
      <Slot
        rNumber="R35820"
        area="b6"
        height="fill"
        image="sal-temp-probe"
        className="px-3 pb-4"
        hubId={hubId}
      />
      <Slot
        rNumber="R35828"
        area="b7"
        image="grounding-probe"
        height="fill"
        className="p-0 px-6 md:px-2 md:pb-3"
        hubId={hubId}
      />
      <Slot
        rNumber="R35823"
        area="b8"
        image="ATO"
        height="fill"
        className="ato p-3 md:p-6"
        position="horizontal"
        hubId={hubId}
      />
      <Slot
        rNumber="R35824"
        area="b9"
        image="leak"
        height="fill"
        className="px-7 py-0 md:p-2"
        hubId={hubId}
      />
      <Slot
        rNumber="R35837"
        area="c1"
        image="probe-holder"
        className="probe-holder pb-3"
        hubId={hubId}
      />

      <Slot
        rNumber="R35827"
        area="m1"
        height="fill"
        image="expander-pro"
        position="horizontal"
        className="expander pb-0 md:pt-8"
        hubId={hubId}
      />

      {isModal && (
        <div style={{ gridArea: 'i1' }} id="config-color-legend">
          <h4 className="mb-2 text-left text-sm font-bold leading-snug text-rsf-black">{t('title')}</h4>
          <ul className="flex flex-col gap-2 *:flex *:flex-row *:items-center *:gap-1">
            <li>
              <div className="m-0 size-3 overflow-hidden rounded-full bg-rsf-green" />
              <span className="break-words text-[8px] leading-snug text-rsf-grey-dark md:text-xs">{t('selected')}</span>
            </li>
            <li>
              <div className="m-0 size-3 overflow-hidden rounded-full bg-rsf-accent" />
              <span className="break-words text-[8px] leading-snug text-rsf-grey-dark md:text-xs">{t('available')}</span>
            </li>
            <li>
              <div className="m-0 size-3 overflow-hidden rounded-full bg-rsf-grey" />
              <span className="break-words text-[8px] leading-snug text-rsf-grey-dark md:text-xs">{t('unavailable')}</span>
            </li>
          </ul>
        </div>
      )}

      <Slot
        rNumber="R35818"
        area="s1"
        image="Deluxe-ROW"
        variants={['usa']}
        position="horizontal"
        className="pb-6"
        hubId={hubId}
      />
      <Slot
        rNumber="R35817"
        area="p2"
        image="Power-ROW"
        variants={['usa', 'uk', 'aus-chn']}
        hubId={hubId}
      />
    </button>
  )
}
